import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { SERVICES } from "@/data/content";
import { Factory, Sun, Building2, Network, Zap, LandPlot, Wrench, FileSpreadsheet, CheckCircle2 } from "lucide-react";

const ICONS = { Factory, Sun, Building2, Network, Zap, LandPlot, Wrench, FileSpreadsheet };

const FEATURE_BULLETS = {
  "utility-scale-epc": ["220 kV / 400 kV evacuation design", "Bifacial + tracker integrations", "BoQ to commissioning under one roof"],
  "pm-kusum": ["Component-A & C developer", "DISCOM-anchored PPAs", "Feeder-level solarisation"],
  "government-solar": ["Empanelled with central & state agencies", "Tender response & EPC delivery", "Long-term O&M support"],
  "open-access": ["Wheeling & banking approvals", "CEIG / STU coordination", "Third-party sale structuring"],
  "captive-solar": ["Group captive (26% equity) structures", "HT industrial offtake design", "End-to-end SPV management"],
  "solar-parks": ["Mode-5 / Mode-8 development", "Land aggregation & DPR", "Pooling substation EPC"],
  "om-services": ["24x7 NOC monitoring", "Drone thermography", "Module cleaning & vegetation"],
  "project-development": ["DPR & financial closure", "PPA bidding support", "Land & approvals desk"],
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        overline="Our Services"
        title="End-to-end renewable EPC & development."
        subtitle="Eight specialised verticals, one execution backbone — designed for utility-scale renewable infrastructure delivery across India."
        image="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        testid="services-hero"
      />

      <section className="section-padding bg-white">
        <div className="container-corp space-y-4">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] || Factory;
            const reverse = idx % 2 === 1;
            return (
              <div
                key={s.slug}
                data-testid={`service-row-${s.slug}`}
                className={`grid lg:grid-cols-12 gap-10 items-center py-10 border-t border-[#E2E8F0] ${
                  reverse ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-6">
                  <img src={s.image} alt={s.title} className="w-full h-80 object-cover" />
                </div>
                <div className="lg:col-span-6">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 bg-[#0B2341] flex items-center justify-center">
                      <Icon className="text-[#16A34A]" size={22} strokeWidth={1.5} />
                    </div>
                    <div className="font-mono text-xs text-[#16A34A] tracking-widest">
                      / {String(idx + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <h3 className="font-heading text-2xl lg:text-3xl font-semibold text-[#0B2341] mb-4 leading-tight">
                    {s.title}
                  </h3>
                  <p className="text-[#4A5568] leading-relaxed mb-6">{s.summary}</p>
                  <ul className="space-y-2.5">
                    {(FEATURE_BULLETS[s.slug] || []).map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-[#0A1118]">
                        <CheckCircle2 size={18} className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTASection
        overline="Need a custom EPC scope?"
        title="Engineer your project with us."
        body="From feasibility to PPA execution and O&M, our development desk responds within 48 hours."
      />
    </div>
  );
}
